"use client"

import { useState } from "react"
import { LawnCostResult } from "@/lib/cost-calculator"
import { trackEvent } from "@/lib/analytics"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import {
  DollarSign,
  TrendingDown,
  CheckCircle,
  Wallet,
  MapPin,
  Ruler,
  Target
} from "lucide-react"
import dynamic from "next/dynamic"
import { ProductRecommendations } from "@/components/tools/product-recommendations"
import {
  ToolDataChartSkeleton,
  UnifiedShareButton,
} from "@/components/tools/shared"
import { buildToolUrl } from "@/lib/result-tool-urls"

const CostDataChart = dynamic(
  () => import("@/components/tools/charts/cost-data-chart").then((m) => m.CostDataChart),
  { ssr: false, loading: () => <ToolDataChartSkeleton height={320} /> }
)

interface CostResultsProps {
  result: LawnCostResult
}

interface CategoryItem {
  description: string
  frequency: string
  totalCost: number
}

function CategorySection({
  name,
  subLabel,
  meta,
  items,
  formatCurrency,
}: {
  name: string
  subLabel?: string
  meta: { percentage: number; amount: number }
  items: CategoryItem[]
  formatCurrency: (amount: number) => string
}) {
  return (
    <div className="p-4 bg-linear-to-br from-green-50/60 to-gray-50 rounded-xl border border-green-100">
      <div className="mb-3">
        <div className="flex justify-between items-baseline gap-3 mb-2">
          <h4 className="font-semibold text-green-900">
            {name}
            {subLabel && <span className="ml-2 text-xs font-normal text-gray-500">({subLabel})</span>}
          </h4>
          <p className="text-sm font-semibold text-green-700 tabular-nums">
            {formatCurrency(meta.amount)}
            <span className="text-xs font-normal text-gray-500 ml-2">{meta.percentage.toFixed(1)}%</span>
          </p>
        </div>
        <div className="w-full bg-gray-100 rounded-full h-1.5 overflow-hidden">
          <div
            className="h-1.5 rounded-full bg-green-500 transition-all"
            style={{ width: `${meta.percentage}%` }}
          />
        </div>
      </div>
      <div className="space-y-2">
        {items.map((item, index) => (
          <div key={index} className="flex justify-between items-center text-sm bg-white p-2 rounded-lg">
            <div>
              <p className="text-gray-700">{item.description}</p>
              <p className="text-xs text-gray-500">{item.frequency}</p>
            </div>
            <p className="font-semibold text-gray-700 tabular-nums">{formatCurrency(item.totalCost)}</p>
          </div>
        ))}
      </div>
    </div>
  )
}

type CostView = "annual" | "monthly"

export function CostResults({ result }: CostResultsProps) {
  const [view, setView] = useState<CostView>("annual")

  if (!result || !result.grassType || !result.region) {
    return null
  }

  const formatCurrency = (amount: number) => {
    return `$${amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
  }

  const breakdownByCategory = new Map(
    result.budgetBreakdown.map(item => [item.category, item])
  )
  const categoryMeta = (name: string) => {
    const item = breakdownByCategory.get(name)
    return {
      percentage: item?.percentage ?? 0,
      amount: item?.amount ?? 0,
    }
  }

  return (
    <>
    <div id="results" className="space-y-6">
      {/* Hero card — brand-green gradient */}
      <Card className="p-6 md:p-8 bg-linear-to-br from-green-600 via-green-500 to-green-700 border-0 text-white overflow-hidden relative shadow-lg">
        {/* Background decoration */}
        <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2" aria-hidden="true" />
        <div className="absolute bottom-0 left-0 w-32 h-32 bg-white/5 rounded-full translate-y-1/2 -translate-x-1/2" aria-hidden="true" />

        <div className="relative">
          <div className="absolute top-0 right-0">
            <UnifiedShareButton
              url={typeof window !== 'undefined'
                ? `${window.location.origin}${buildToolUrl('lawn-cost-estimator', {
                    zip: result.zipCode,
                    grassType: result.grassType,
                    lawnSize: result.lawnSize,
                    serviceLevel: result.serviceLevel,
                    careApproach: result.careApproach,
                  })}`
                : ''}
              title="Lawn Care Cost Estimate"
              trackingId="lawn-cost-estimator"
              text={`Annual lawn care cost estimate for ${result.lawnSize.toLocaleString()} sq ft`}
              variant="hero-overlay"
              size="sm"
            />
          </div>
          <div className="flex flex-col md:flex-row items-center md:items-start gap-6">
            {/* Icon */}
            <div className="flex-shrink-0 p-4 bg-white/20 backdrop-blur-xs rounded-2xl">
              <Wallet className="w-12 h-12 md:w-16 md:h-16" />
            </div>

            <div className="flex-1 text-center md:text-left">
              {/* In-result scenario toggle: reframe the hero between annual and
                  monthly without sending the user back to the form. */}
              <div
                role="group"
                aria-label="Show cost as annual or monthly"
                className="mb-3 inline-flex rounded-full bg-white/15 p-1 backdrop-blur-xs"
              >
                {([
                  { id: "annual", label: "Per year" },
                  { id: "monthly", label: "Per month" },
                ] as const).map((opt) => {
                  const active = view === opt.id
                  return (
                    <button
                      key={opt.id}
                      type="button"
                      onClick={() => {
                        setView(opt.id)
                        trackEvent("cost_view_toggled", {
                          view: opt.id,
                          grass_type: result.grassType,
                          care_approach: result.careApproach,
                        })
                      }}
                      aria-pressed={active}
                      className={`rounded-full px-4 py-1.5 text-sm font-semibold transition-colors ${
                        active
                          ? "bg-white text-green-700 shadow-sm"
                          : "text-white/80 hover:text-white"
                      }`}
                    >
                      {opt.label}
                    </button>
                  )
                })}
              </div>

              <h2 className="text-2xl md:text-4xl font-bold mb-2 tabular-nums">
                {formatCurrency(view === "annual" ? result.annualTotal : result.monthlyTotal)}
              </h2>
              <p className="text-white/80 text-lg mb-4">
                {view === "annual"
                  ? "Annual Lawn Care Budget"
                  : "Monthly Lawn Care Budget"}
              </p>

              {/* Info Pills */}
              <div className="flex flex-wrap gap-3 justify-center md:justify-start">
                <span className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-xs px-3 py-1.5 rounded-full text-sm">
                  <Target className="w-4 h-4" />
                  {result.grassType}
                </span>
                <span className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-xs px-3 py-1.5 rounded-full text-sm">
                  <MapPin className="w-4 h-4" />
                  {result.region}
                </span>
                <span className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-xs px-3 py-1.5 rounded-full text-sm">
                  <Ruler className="w-4 h-4" />
                  {result.lawnSize.toLocaleString()} sq ft
                </span>
              </div>
            </div>
          </div>

          {/* Quick Metrics Grid */}
          <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 sm:gap-3">
            <div className="bg-white/15 backdrop-blur-xs rounded-xl p-3 text-center">
              <p className="text-white/70 text-xs uppercase tracking-wider mb-1">
                {view === "annual" ? "Monthly" : "Annual"}
              </p>
              <p className="font-bold text-lg md:text-xl tabular-nums">
                {formatCurrency(view === "annual" ? result.monthlyTotal : result.annualTotal)}
              </p>
            </div>
            <div className="bg-white/15 backdrop-blur-xs rounded-xl p-3 text-center">
              <p className="text-white/70 text-xs uppercase tracking-wider mb-1">Per 1000 sq ft</p>
              <p className="font-bold text-lg md:text-xl">{formatCurrency(result.costPer1000SqFt)}</p>
            </div>
            <div className="bg-white/15 backdrop-blur-xs rounded-xl p-3 text-center">
              <p className="text-white/70 text-xs uppercase tracking-wider mb-1">Service</p>
              <p className="font-bold text-sm md:text-base capitalize">{result.serviceLevel}</p>
            </div>
            <div className="bg-white/15 backdrop-blur-xs rounded-xl p-3 text-center">
              <p className="text-white/70 text-xs uppercase tracking-wider mb-1">Approach</p>
              <p className="font-bold text-sm md:text-base capitalize">
                {result.careApproach === 'diy' ? 'DIY' : result.careApproach}
              </p>
            </div>
          </div>

          {/* Savings Banner */}
          {result.savingsVsProfessional && result.savingsVsProfessional > 0 && (
            <div className="mt-4 bg-white/20 backdrop-blur-xs rounded-xl p-4">
              <div className="flex items-center gap-3">
                <TrendingDown className="w-5 h-5 flex-shrink-0" />
                <div>
                  <p className="text-sm text-white/70 mb-1">You save vs professional service</p>
                  <p className="font-bold text-xl">
                    {formatCurrency(result.savingsVsProfessional)}
                    <span className="text-sm font-normal text-white/70 ml-2">
                      ({((result.savingsVsProfessional / result.professionalTotal!) * 100).toFixed(0)}% savings)
                    </span>
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </Card>

      {/* Cost Breakdown */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <DollarSign className="w-5 h-5 text-green-600" />
            Cost Breakdown
          </CardTitle>
          <CardDescription>
            Where your annual budget goes
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {result.fertilizationCosts.length > 0 && (
            <CategorySection
              name="Fertilization"
              meta={categoryMeta('Fertilization')}
              items={result.fertilizationCosts}
              formatCurrency={formatCurrency}
            />
          )}

          {result.maintenanceCosts.length > 0 && (
            <CategorySection
              name="Maintenance"
              meta={categoryMeta('Maintenance')}
              items={result.maintenanceCosts}
              formatCurrency={formatCurrency}
            />
          )}

          {result.treatmentCosts.length > 0 && (
            <CategorySection
              name="Treatments"
              meta={categoryMeta('Treatments')}
              items={result.treatmentCosts}
              formatCurrency={formatCurrency}
            />
          )}

          {result.equipmentCosts && result.equipmentCosts.length > 0 && (
            <CategorySection
              name="Equipment"
              subLabel="Amortized"
              meta={categoryMeta('Equipment')}
              items={result.equipmentCosts}
              formatCurrency={formatCurrency}
            />
          )}

          {result.waterCosts && (
            <CategorySection
              name="Water"
              meta={categoryMeta('Water')}
              items={[result.waterCosts]}
              formatCurrency={formatCurrency}
            />
          )}
        </CardContent>
      </Card>

      {/* Cost Comparison */}
      {(result.diyTotal || result.professionalTotal) && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingDown className="w-5 h-5 text-green-600" />
              Cost Comparison
            </CardTitle>
            <CardDescription>
              Compare different care approaches
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {result.diyTotal && (
                <div className="text-center p-5 bg-linear-to-br from-green-50 to-gray-50 rounded-xl border border-green-200">
                  <p className="text-sm text-gray-600 mb-2">DIY Approach</p>
                  <p className="text-3xl font-bold text-green-600">{formatCurrency(result.diyTotal)}</p>
                  <p className="text-xs text-gray-500 mt-2">Materials only</p>
                </div>
              )}

              <div className="text-center p-5 bg-linear-to-br from-green-100 to-green-50 rounded-xl border-2 border-green-400">
                <p className="text-sm text-green-700 font-medium mb-2">Your Selection</p>
                <p className="text-3xl font-bold text-green-700">{formatCurrency(result.annualTotal)}</p>
                <p className="text-xs text-green-600 mt-2 capitalize">
                  {result.careApproach === 'diy' ? 'DIY' : result.careApproach} approach
                </p>
              </div>

              {result.professionalTotal && (
                <div className="text-center p-5 bg-linear-to-br from-gray-50 to-slate-50 rounded-xl border border-gray-200">
                  <p className="text-sm text-gray-600 mb-2">Professional</p>
                  <p className="text-3xl font-bold text-green-600">{formatCurrency(result.professionalTotal)}</p>
                  <p className="text-xs text-gray-500 mt-2">Full service</p>
                </div>
              )}
            </div>

          </CardContent>
        </Card>
      )}

      {/* Cost Saving Tips */}
      <Card className="border-green-200">
        <CardHeader className="bg-green-50 rounded-t-lg">
          <CardTitle className="flex items-center gap-2 text-green-800">
            <TrendingDown className="w-5 h-5" />
            Cost Saving Tips
          </CardTitle>
          <CardDescription>
            Ways to reduce your lawn care expenses
          </CardDescription>
        </CardHeader>
        <CardContent className="pt-4">
          <ul className="space-y-2">
            {result.costSavingTips.map((tip, index) => (
              <li key={index} className="flex items-start gap-3 text-sm p-2 bg-green-50/50 rounded-lg">
                <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                <span className="text-gray-700">{tip}</span>
              </li>
            ))}
          </ul>
        </CardContent>
      </Card>

      {/* Product Recommendations */}
      <ProductRecommendations
        categories={["Fertilizers & Soil Health", "Lawn Mowers & Maintenance Tools", "Watering & Irrigation"]}
        title="Budget-Friendly Products"
        description="Quality lawn care products to help manage your annual costs"
        trackingContext="cost-calculator"
        icon={<DollarSign className="w-5 h-5 text-green-600" />}
        keywords={['fertilizer', 'lawn food', result.grassType, result.careApproach].filter(Boolean)}
        compact
      />

      {/* The data behind your budget */}
      <CostDataChart result={result} />

    </div>
    </>
  )
}
